import { isDefined } from '../../../utilities/is-defined';
import { ConfigConstants } from '../../../config/config-constants';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../../models/complex/time-recording-entry.model';
import { CONFIRMATION_TYPES } from '../../../enums/confirmation-types.enum';
import { AddressState } from '../../address/address-collection.state';
import { configValueIncludes } from '../../config/selectors/config.selector';
import {
    completionKeyOf,
    customerNameOf,
    resolveNotificationAddresses,
} from '../../notification/notification-collection.converters';
import { NotificationCompletionsState, NotificationState } from '../../notification/notification-collection.state';
import {
    effectiveStatusForEntry,
    isInProgressOrToBeSignedCode,
} from '../../notification/selectors/notification.selector';
import {
    ConfirmationTimeRecordingState,
    TimeRecordingEntryState,
} from '../../confirmation/sections/confirmation-time-recording.state';
import {
    ConfirmationEntryDerivationFlags,
    ConfirmationTimesheetContext,
} from '../../utilities/confirmation-timesheet-context';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { TimesheetState, TimesheetWorkEntryState } from '../timesheet-collection.state';
import { deriveStatusWorkEntries } from './derive-status-work-entries';
import { withSortedEntries } from './sorted-entries';

/**
 * Derives a confirmation's timesheet work entries from its time-recording section.
 *
 * This is the single transformation behind both directions the timesheet gets confirmation data:
 * the read-time projection of a confirmation that is still open, and the commit that lands the
 * entries in the slice when the confirmation is submitted. The two differ only in the context they
 * pass — an open confirmation carries no distance and is not submitted — so keeping one function
 * means the rows a technician sees while working cannot drift from the ones that get stored.
 *
 * Model-free by design (see `architecture-rules.md`): it reads the plain section state rather than
 * `TimeRecordingEntryModel`, so `kind` replaces the `instanceof` checks the service-side
 * transformation used, and the whole derivation is testable without an injector.
 */

/** The confirmation-level facts every derived entry is stamped with. */
export type DerivedEntryContext = Readonly<{
    customerName: string;
    notificationType: ConfigConstants.NotificationTypes;
    confirmationType: CONFIRMATION_TYPES;
    /**
     * The confirmation's travel distance, attached to the first travel-to entry only.
     *
     * An open confirmation passes `0`: the autosave path never carried a distance, only an empty
     * placeholder, so in-progress rows have always shown none. The real value arrives with the
     * submit that commits the entries.
     */
    distance: number;
    isConfirmationSubmitted: boolean;
    flags: ConfirmationEntryDerivationFlags;
}>;

/**
 * The work code an entry books against.
 *
 * A travel-back entry becomes standby travel when config enables it, which is why the raw
 * `typeOfWork` is not stored directly. The `?? ''` guard covers a general entry whose type of work
 * was never selected: the section state types the field as required but the converter passes the
 * stored value through unchecked, and an absent one must not reach the store as `undefined`, which
 * the serializability check rejects.
 *
 * @param entry the recorded entry
 * @param enableStandbyTravel whether config enables standby travel
 * @returns the work code to book
 */
function workCodeOf(entry: TimeRecordingEntryState, enableStandbyTravel: boolean): string {
    if (enableStandbyTravel && entry.kind === 'limited' && entry.typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
        return TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
    }
    return entry.typeOfWork ?? '';
}

/**
 * Derives the work entries a confirmation's time recording amounts to.
 *
 * The distance rides on the *first* travel-to entry alone, and that entry is also never billable —
 * both are the service-side transformation's behaviour, kept so the committed rows are unchanged by
 * the move behind the store. `activityType` has always been empty and `addressInOutModel` always
 * `false`; neither is read back for a work entry.
 *
 * `afterHourCallOut` is the technician's answer on the confirmation, read only when config asks the
 * question. It rides on every entry although it is one fact about the confirmation, because that is
 * where the submit out model reads it: an on-site entry with the flag set books under a different SAP
 * work code. The legacy transformation computed this and then dropped it while serializing, so the
 * flag never reached a submitted timesheet.
 *
 * The entry now records the address's id rather than a copy of it — the address itself lives in the
 * address slice, which is where a consumer resolves the id against.
 *
 * @param timeRecording the confirmation's time-recording section
 * @param notificationId the notification the entries are booked against
 * @param address the notification's address
 * @param context the confirmation-level facts and config switches
 * @param makeEntryId mints the entry key for the entry at the given index — a stable synthetic key
 * for a read-time projection, a fresh id for a commit
 * @returns the derived work entries, in recording order
 */
export function deriveWorkEntriesForConfirmation(
    timeRecording: ConfirmationTimeRecordingState,
    notificationId: NotificationIdState,
    address: AddressState | undefined,
    context: DerivedEntryContext,
    makeEntryId: (index: number) => string,
): TimesheetWorkEntryState[] {
    const addressId = address?.id ?? null;
    let distanceAttached = false;

    return timeRecording.timeRecordingEntries.map((entry, index) => {
        const isLimited = entry.kind === 'limited';
        const carriesDistance =
            !distanceAttached && isLimited && entry.typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO;
        distanceAttached = distanceAttached || carriesDistance;

        return {
            kind: 'work',
            entryId: makeEntryId(index),
            notificationId,
            start: entry.startDateTime,
            end: entry.endDateTime,
            workCode: workCodeOf(entry, context.flags.enableStandbyTravel),
            activityType: '',
            meansOfTransport: isLimited && context.flags.displayMeansOfTransport ? entry.meansOfTransport : '',
            distance: carriesDistance ? context.distance : 0,
            address: addressId,
            addressInOutModel: false,
            isTravelBillable: carriesDistance
                ? false
                : isLimited && context.flags.enableStandbyTravel && context.flags.isTravelBillable,
            afterHourCallOut: context.flags.enabledAfterHoursCallOut && timeRecording.afterHourCallOutFlag,
            customerName: context.customerName,
            notificationType: context.notificationType,
            confirmationType: context.confirmationType,
            isConfirmationSubmitted: context.isConfirmationSubmitted,
        };
    });
}

/**
 * Replaces one notification's work entries, leaving every other entry alone.
 *
 * Manual entries carry a notification id too — the "add travel back" ones — but they belong to the
 * user rather than to the confirmation, so only work entries are replaced. The result is re-sorted
 * because chronological order is a slice invariant readers rely on.
 *
 * @param timesheet the timesheet to update
 * @param notificationId the notification whose work entries are being replaced
 * @param entries the replacement work entries
 * @returns the timesheet with the notification's work entries swapped, in chronological order
 */
export function replaceNotificationWorkEntries(
    timesheet: TimesheetState,
    notificationId: NotificationIdState,
    entries: readonly TimesheetWorkEntryState[],
): TimesheetState {
    const entriesOfOtherOrigin = timesheet.entries.filter(
        (entry) => entry.kind !== 'work' || !NotificationStateUtilities.compare(entry.notificationId, notificationId),
    );
    return withSortedEntries({ ...timesheet, entries: [...entriesOfOtherOrigin, ...entries] });
}

/**
 * The config values a derivation needs, read once for the whole collection.
 *
 * `travelBillableOrderTypes` is the raw config value rather than a resolved boolean, because one
 * derivation spans confirmations of different order types and must answer the question per
 * notification — which a factory selector per notification could not do without losing its
 * memoization.
 */
export type TimesheetDerivationConfig = Readonly<{
    enableStandbyTravel: boolean;
    displayMeansOfTransport: boolean;
    travelBillableOrderTypes: string | undefined;
    enabledAfterHoursCallOut: boolean;
    /** Whether config books a notification worked remotely under its own timesheet codes. */
    enableRemoteWork: boolean;
}>;

/**
 * The surroundings of one confirmation, resolved against whole collections.
 *
 * Shared by the commit path, which needs it for a single notification, and the read-time projection,
 * which needs it for every open confirmation at once — so the two cannot disagree about what a
 * confirmation's customer or billability is.
 *
 * @param entry the confirmation's notification, absent when the collection does not hold it
 * @param addresses the address collection to resolve the customer against
 * @param config the config values the derivation branches on
 * @returns the confirmation's timesheet context
 */
export function timesheetContextFor(
    entry: NotificationState | undefined,
    addresses: readonly AddressState[],
    config: TimesheetDerivationConfig,
): ConfirmationTimesheetContext {
    const resolved = entry ? resolveNotificationAddresses(entry, addresses) : undefined;
    return {
        customerName: customerNameOf(resolved?.customer),
        // A notification the collection no longer holds leaves the type unknown, the same way an
        // unresolvable customer address leaves the name empty.
        notificationType: entry?.notificationType ?? ConfigConstants.NotificationTypes.Unknown,
        address: resolved?.customer ?? null,
        flags: {
            enableStandbyTravel: config.enableStandbyTravel,
            displayMeansOfTransport: config.displayMeansOfTransport,
            isTravelBillable: configValueIncludes(config.travelBillableOrderTypes, entry?.orderType ?? ''),
            enabledAfterHoursCallOut: config.enabledAfterHoursCallOut,
        },
    };
}

/** A confirmation as the read-time projection needs it: its identity and the times recorded on it. */
export type InProgressConfirmation = Readonly<{
    notificationId: NotificationIdState;
    confirmationType: CONFIRMATION_TYPES;
    timeRecording?: ConfirmationTimeRecordingState;
}>;

/**
 * The key a derived row is tracked and addressed by.
 *
 * Deterministic, so the same confirmation yields the same keys on every recomputation and `@for`
 * does not tear down every row when one changes — a uuid would, and a projector may not mint one
 * anyway. Namespaced so it can never collide with the uuid a committed entry carries, which also
 * lets a write path recognise a row that answers to no stored entry.
 *
 * @param confirmation the confirmation the entry was derived from
 * @param index the entry's position in the confirmation's time recording
 * @returns the synthetic entry key
 */
export function derivedEntryId(confirmation: InProgressConfirmation, index: number): string {
    const { frontId, qmNum, maNum } = confirmation.notificationId;
    return `derived:${frontId}:${qmNum}:${maNum}:${confirmation.confirmationType}:${index}`;
}

/**
 * The work entries every open confirmation currently amounts to, plus — for a notification that is
 * in progress or to be signed but has no live confirmation carrying a time-recording section yet —
 * the entries its recorded statuses amount to.
 *
 * The confirmation is derived from directly (see {@link deriveWorkEntriesForConfirmation}) because
 * it is the technician's own recorded times and always wins. The status fallback exists because a
 * notification can be worked before its confirmation screen has ever been opened — nothing has
 * autosaved a time-recording section yet — and the technician still needs to see that time occupied
 * in the timesheet, the same gap `addInitialTSEntriesForNotification` used to fill for the legacy
 * push. Derived rather than stored either way, so what the timesheet shows cannot go stale against
 * the confirmation or the notification's statuses.
 *
 * Takes whole collections and does its own lookups. Resolving each confirmation or notification
 * through a per-notification factory selector instead would build a fresh selector per call and so
 * miss its memo every time.
 *
 * @param confirmations the open confirmations
 * @param notifications the notification collection to resolve each confirmation against, and to
 * fall back to for a notification without one
 * @param completions the completion map — a completed notification derives nothing
 * @param addresses the address collection
 * @param config the config values the derivation branches on
 * @param now the current instant, as an ISO timestamp — see `deriveStatusWorkEntries`
 * @returns the derived work entries of every open confirmation and every confirmation-less
 * in-progress notification
 */
export function deriveInProgressWorkEntries(
    confirmations: readonly InProgressConfirmation[],
    notifications: readonly NotificationState[],
    completions: NotificationCompletionsState,
    addresses: readonly AddressState[],
    config: TimesheetDerivationConfig,
    now: string,
): TimesheetWorkEntryState[] {
    const isCompleted = (id: NotificationIdState): boolean => completionKeyOf(id) in completions;
    const fromConfirmations = confirmations.flatMap((confirmation) => {
        if (!confirmation.timeRecording) {
            return [];
        }
        const entry = notifications.find((candidate) =>
            NotificationStateUtilities.compare(candidate.notificationId, confirmation.notificationId),
        );
        // A completed notification's times are already committed, so nothing here is in progress —
        // the same question the push this replaced asked as `isNotificationCompleted`.
        //
        // The confirmation can still be in the slice at this point: submitting rebuilds the model
        // from the snapshot, so `isSubmitted` is set on that copy rather than on the one the screen
        // holds, and leaving the screen saves it once more. Without this the re-saved confirmation
        // would keep deriving, and a live confirmation wins over a committed entry — so a submitted
        // row would go on reading as in progress.
        //
        // vice versa, with some force-checkin moves the notification can already vanish from the
        // NdpHeaderSet, but the user still has an orphan confirmation, which should be ignored here.
        if (!isDefined(entry) || isCompleted(entry.notificationId)) {
            return [];
        }
        const context = timesheetContextFor(entry, addresses, config);
        return deriveWorkEntriesForConfirmation(
            confirmation.timeRecording,
            confirmation.notificationId,
            context.address ?? undefined,
            {
                customerName: context.customerName,
                notificationType: context.notificationType,
                confirmationType: confirmation.confirmationType,
                distance: 0,
                isConfirmationSubmitted: false,
                flags: context.flags,
            },
            (index) => derivedEntryId(confirmation, index),
        );
    });

    const fromStatuses = notifications.flatMap((entry) => {
        // Same reasoning as above: a completed notification's times are already committed.
        if (isCompleted(entry.notificationId)) {
            return [];
        }
        // A live confirmation with a time-recording section always wins — checked for
        // `timeRecording` rather than mere presence, because a confirmation the technician has
        // opened but not yet advanced to the time-recording step carries no such section yet, and
        // falling back to the statuses in that case is deliberate.
        const hasLiveConfirmation = confirmations.some(
            (confirmation) =>
                NotificationStateUtilities.compare(confirmation.notificationId, entry.notificationId) &&
                confirmation.timeRecording,
        );
        if (hasLiveConfirmation) {
            return [];
        }
        if (!isInProgressOrToBeSignedCode(effectiveStatusForEntry(entry).statusCode)) {
            return [];
        }
        const context = timesheetContextFor(entry, addresses, config);
        return deriveStatusWorkEntries(entry, context, config.enableRemoteWork, now);
    });

    return [...fromConfirmations, ...fromStatuses];
}

/**
 * The timesheet as it reads with the open confirmations' entries merged in.
 *
 * A live confirmation wins: any committed work entry of a notification that is currently deriving is
 * dropped in favour of the derived one. That covers the handoff around a submit, where the commit has
 * landed but the confirmation has not been removed yet, and it also hides a stale copy an older build
 * may have left in storage — so neither is ever counted twice.
 *
 * Returns the timesheet itself when nothing derives, so readers downstream keep their memo instead of
 * rebuilding rows against an identical copy.
 *
 * @param timesheet the timesheet as the slice holds it
 * @param derived the work entries the open confirmations currently amount to
 * @returns the timesheet including the derived entries, in chronological order
 */
export function withInProgressEntries(
    timesheet: TimesheetState,
    derived: readonly TimesheetWorkEntryState[],
): TimesheetState {
    if (derived.length === 0) {
        return timesheet;
    }
    const kept = timesheet.entries.filter(
        (entry) =>
            entry.kind !== 'work' ||
            !derived.some((derivedEntry) =>
                NotificationStateUtilities.compare(entry.notificationId, derivedEntry.notificationId),
            ),
    );
    return withSortedEntries({ ...timesheet, entries: [...kept, ...derived] });
}
